import { useQuery } from '@apollo/client';
import { getListTableColumns } from '@openx/components-segment/lib/SegmentsList/tableColumns';
import type { UseSegmentTableFetch } from '@openx/components-segment/lib/utils/context/SegmentHookTypes';
import type { FiltersConfig } from '@openx/types/chipFilters';
import type { ListSegment } from '@openx/types/segments';
import type { Columns, RowActionProps } from '@openx/types/tableHelpers';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../components/AuthContext/context';
import {
  GetProvidersListDocument,
  type GetProvidersListQuery,
  type GetProvidersListQueryVariables,
} from '../../../graphql/query/providers/GetProvidersList.generated';
import {
  GetDistinctSegmentCountryCodesDocument,
  type GetDistinctSegmentCountryCodesQuery,
  type GetDistinctSegmentCountryCodesQueryVariables,
} from '../../../graphql/query/segments/GetDistinctSegmentCountryCodes.generated';
import {
  GetDistinctSegmentRegionCodesDocument,
  type GetDistinctSegmentRegionCodesQuery,
  type GetDistinctSegmentRegionCodesQueryVariables,
} from '../../../graphql/query/segments/GetDistinctSegmentRegionCodes.generated';
import {
  GetDistinctSegmentStatusesDocument,
  type GetDistinctSegmentStatusesQuery,
  type GetDistinctSegmentStatusesQueryVariables,
} from '../../../graphql/query/segments/GetDistinctSegmentStatuses.generated';
import {
  GetSegmentsAccountsDocument,
  type GetSegmentsAccountsQuery,
  type GetSegmentsAccountsQueryVariables,
} from '../../../graphql/query/segments/GetSegmentsAccounts.generated';
import {
  GetTaxonomyMetadataDocument,
  type GetTaxonomyMetadataQuery,
  type GetTaxonomyMetadataQueryVariables,
} from '../../../graphql/query/taxonomy/GetTaxonomyMetadata.generated';
import { useSegmentRowActions } from '../../../modules/segments/hooks/useSegmentRowActions';
import { isInternalUser } from '../../../permissions/validators';
import { mapTaxonomyFilters } from '../../../utils/mapTaxonomyFilters';
import { getFiltersConfig } from './utils/getFiltersConfig';
import { reduceSegmentStatuses, type SegmentStatusReduced } from './utils/reduceSegmentStatuses';

export const useSegmentTableFetch: UseSegmentTableFetch = (phrase: string) => {
  const { t } = useTranslation();
  const { session } = useAuth();
  const { data: accountsData, loading: accountsLoading } = useQuery<
    GetSegmentsAccountsQuery,
    GetSegmentsAccountsQueryVariables
  >(GetSegmentsAccountsDocument);
  const { data: providersData, loading: providersLoading } = useQuery<
    GetProvidersListQuery,
    GetProvidersListQueryVariables
  >(GetProvidersListDocument);
  const { data: statusesData, loading: statusesLoading } = useQuery<
    GetDistinctSegmentStatusesQuery,
    GetDistinctSegmentStatusesQueryVariables
  >(GetDistinctSegmentStatusesDocument);
  const accounts = accountsData?.account ?? [];
  const providers = providersData?.provider ?? [];
  const segmentStatuses = statusesData?.segment_statuses ?? [];
  const { data: regionData, loading: regionsLoading } = useQuery<
    GetDistinctSegmentRegionCodesQuery,
    GetDistinctSegmentRegionCodesQueryVariables
  >(GetDistinctSegmentRegionCodesDocument);
  const { data: countryData, loading: countriesLoading } = useQuery<
    GetDistinctSegmentCountryCodesQuery,
    GetDistinctSegmentCountryCodesQueryVariables
  >(GetDistinctSegmentCountryCodesDocument);
  const regions = regionData?.segment_regions ?? [];
  const countries = countryData?.segment_countries ?? [];
  const { data: taxonomyData, loading: metadataLoading } = useQuery<
    GetTaxonomyMetadataQuery,
    GetTaxonomyMetadataQueryVariables
  >(GetTaxonomyMetadataDocument);
  const metadata = taxonomyData?.segment_aggregate?.nodes ?? [];

  const rowActions: RowActionProps<ListSegment>[] = useSegmentRowActions();

  const { categories, subCategories, types } = useMemo(() => mapTaxonomyFilters(metadata), [metadata]);

  const statuses: SegmentStatusReduced[] = useMemo(() => reduceSegmentStatuses(segmentStatuses), [segmentStatuses]);

  const columns: Columns<ListSegment> = useMemo(() => {
    const tableColumns = getListTableColumns(t, phrase);

    if (!isInternalUser(session)) {
      return tableColumns.filter(({ key }) => key !== 'account');
    }

    return tableColumns;
  }, [t, session, phrase]);

  const filtersConfig: FiltersConfig = useMemo(() => {
    return getFiltersConfig({
      t,
      accounts,
      providers,
      statuses,
      regions,
      countries,
      accountsLoading,
      providersLoading,
      statusesLoading,
      regionsLoading,
      countriesLoading,
      categories,
      subCategories,
      types,
      metadataLoading,
    });
  }, [
    t,
    accounts,
    providers,
    statuses,
    regions,
    countries,
    accountsLoading,
    providersLoading,
    statusesLoading,
    regionsLoading,
    countriesLoading,
    categories,
    subCategories,
    types,
    metadataLoading,
  ]);

  return {
    columns,
    filtersConfig,
    rowActions,
  };
};
